package gr.tech.site.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.styles.SocialsIconStyle
import gr.tech.site.styles.Theme
import gr.tech.site.toSitePalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.*

val FooterLinkStyle = CssStyle.base {
    Modifier
        .color(Theme.DarkGreen.rgb)
        .opacity(75.percent)
        .fontSize(0.875.cssRem)
}

val FooterStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().nearBackground)
        .padding(topBottom = 1.5.cssRem, leftRight = 10.percent)
}

@Composable
fun Footer(modifier: Modifier = Modifier) {
    Footer(
        attrs = FooterStyle.toModifier().then(modifier)
            .classNames("py-5", "w-100")
            .backgroundColor(Colors.White)
            .role("contentinfo").toAttrs()
    ) {
        Column(
            modifier = Modifier
                .classNames(),
            horizontalAlignment = Alignment.CenterHorizontally
        )  {

            Div(
                attrs = Modifier
                    .classNames(
                        "d-flex",
                        "flex-column", "flex-lg-row",
                        "align-items-center",
                        "gap-4", "gap-lg-5",
                        "w-100"
                    )
                    .toAttrs(),
            ) {

                Img(
                    src = "/grlogocenter.webp",
                    alt = "Green Rebates Logo",
                    attrs = Modifier
                        .height(3.cssRem)
                        .display(DisplayStyle.Block)
                        .margin(leftRight = autoLength)
                        .toAttrs {
                            attr("loading", "lazy")
                        }
                )

                Spacer()

                P(
                    attrs = Modifier.classNames("fs-6", "mb-0").color(Theme.DarkGreen.rgb).toAttrs()
                ) {
                    Text("Follow Us")
                }

                Div(
                    attrs = Modifier
                        .classNames("d-flex", "justify-content-center", "align-items-center", "gap-4")
                        .toAttrs()
                ) {
                    Link(
                        path = "https://www.facebook.com/greenrebates/",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "Read more on our Facebook page")
                            }
                    ) {
                        FaFacebook(
                            size = IconSize.LG
                        )
                    }

                    Link(
                        path = "https://www.linkedin.com/company/green-rebates",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "Read more on our LinkedIn page")
                            }
                    ) {
                        FaLinkedin(
                            size = IconSize.LG
                        )
                    }

                    Link(
                        path = "https://www.instagram.com/greenrebates/",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "Read more on our Instagram page")
                            }
                    ) {
                        FaInstagram(
                            size = IconSize.LG
                        )
                    }

                    Link(
                        path = "https://www.tiktok.com/@green.rebates",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "Read more on our Tiktok page")
                            }
                    ) {
                        FaTiktok(
                            size = IconSize.LG
                        )
                    }

                    Link(
                        path = "https://www.youtube.com/channel/UCjREfEKvNsrM0wyNN_aQaEw",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "See more on our YouTube channel")
                            }
                    ) {
                        FaYoutube(
                            size = IconSize.LG
                        )
                    }

                    Link(
                        path = "https://x.com/greenrebates1",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "Read more on our X/Twitter page")
                            }
                    ) {
                        FaXTwitter(
                            size = IconSize.LG
                        )
                    }
                }
            }

            // ── Site Links Grid ──────────────────────────────────────────────
            HorizontalDivider()

            Div(attrs = Modifier.classNames("row", "w-100", "py-4", "g-4").toAttrs()) {

                // Column 1 — Services
                Div(attrs = Modifier.classNames("col-6", "col-lg-3").toAttrs()) {
                    P(attrs = Modifier.classNames("fw-bold", "mb-2").color(Theme.DarkGreen.rgb).toAttrs()) {
                        Text("Services")
                    }
                    listOf(
                        "/our-services"            to "Our Services",
                        "/led-grow-light-rebates"  to "LED Grow Light Rebates",
                        "/dehumidifier-rebates"    to "Dehumidifier Rebates",
                        "/hvac-rebates"            to "HVAC Rebates",
                        "/financing"               to "Financing",
                    ).forEach { (path, label) ->
                        Div(attrs = Modifier.classNames("mb-1").toAttrs()) {
                            Link(
                                path = path,
                                text = label,
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = FooterLinkStyle.toModifier()
                            )
                        }
                    }
                }

                // Column 2 — Company
                Div(attrs = Modifier.classNames("col-6", "col-lg-3").toAttrs()) {
                    P(attrs = Modifier.classNames("fw-bold", "mb-2").color(Theme.DarkGreen.rgb).toAttrs()) {
                        Text("Company")
                    }
                    listOf(
                        "/meet-the-team" to "Meet the Team",
                        "/partners"      to "Partners",
                        "/blog"          to "Blog",
                        "/article"      to "Articles",
                        "/contact-us"    to "Contact Us",
                    ).forEach { (path, label) ->
                        Div(attrs = Modifier.classNames("mb-1").toAttrs()) {
                            Link(
                                path = path,
                                text = label,
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = FooterLinkStyle.toModifier()
                            )
                        }
                    }
                }

                // Column 3 — Rebate Programs
                Div(attrs = Modifier.classNames("col-6", "col-lg-3").toAttrs()) {
                    P(attrs = Modifier.classNames("fw-bold", "mb-2").color(Theme.DarkGreen.rgb).toAttrs()) {
                        Text("Rebate Programs")
                    }
                    listOf(
                        "/cannabis-rebates"       to "Cannabis Rebates",
                        "/led-grow-light-rebates" to "LED Lighting",
                        "/states"   to "View States",
                    ).forEach { (path, label) ->
                        Div(attrs = Modifier.classNames("mb-1").toAttrs()) {
                            Link(
                                path = path,
                                text = label,
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = FooterLinkStyle.toModifier()
                            )
                        }
                    }
                }

                // Column 4 — Legal & Contact
                Div(attrs = Modifier.classNames("col-6", "col-lg-3").toAttrs()) {
                    P(attrs = Modifier.classNames("fw-bold", "mb-2").color(Theme.DarkGreen.rgb).toAttrs()) {
                        Text("Legal")
                    }
                    listOf(
                        "/privacy"    to "Privacy Policy",
                        "/terms"  to "Terms & Conditions",
                        "/contact-us"        to "Contact Us",
                    ).forEach { (path, label) ->
                        Div(attrs = Modifier.classNames("mb-1").toAttrs()) {
                            Link(
                                path = path,
                                text = label,
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = FooterLinkStyle.toModifier()
                            )
                        }
                    }
                }
            }

            HorizontalDivider()

            P(
                attrs = Modifier.classNames("fs-6", "mb-0", "fw-light", "text-center").color(Theme.DarkGreen.rgb).toAttrs()
            ) {
                Text("Copyright © 2026 Green Rebates. All Rights Reserved.")
            }

        }
    }
}
